/**
 * Экран «подпишись» и путь обратно · окно 3 запуска
 *
 * Нет подписки — приложение закрыто ЦЕЛИКОМ: ни карточек, ни навигации, ни статистики.
 * Но человек не должен уйти с ощущением «всё пропало», поэтому на экране ровно три
 * вещи: почему закрыто · куда идти · обещание, что прогресс на месте (это правда —
 * ядро при закрытии доступа не трогает ни одной строки).
 *
 * ⭐ ДВЕ РАЗНЫЕ ПРИЧИНЫ, и путать их нельзя:
 *   `no_subscription` — Telegram ответил «этого человека в канале нет» → «подпишись»;
 *   `unverified`      — Telegram не ответил вовсе → «не смогли проверить».
 * Показать второму «подпишись» значит обвинить человека в том, чего он не делал.
 *
 * ⭐ Возврат БЕЗ перезахода: кнопка «Я подписался — проверить» зовёт тот же `POST /auth`,
 * и если доступ открылся, приложение продолжает загрузку с того же места (`main.ts`).
 *
 * ⛔ Модуль намеренно без импортов рантайма (SDK Telegram зовём через `window` внутри
 * функции): так его целиком собирает и гоняет `tools/check-access.mjs` без браузера.
 */

import type { Access } from "./api";

export type GateReason = "no_subscription" | "unverified";

export interface GateAction {
  label: string;
  act: "check" | "open";
}

export interface GateView {
  reason: GateReason;
  title: string;
  lines: string[];
  primary: GateAction;
  secondary: GateAction | null;
  url: string | null;
  /** Что сказать, если нажали «проверить», а доступа всё ещё нет. */
  retryHint: string;
}

/** Обещание, которое одинаково верно в обоих случаях — и это ПРАВДА, а не утешение. */
const KEPT =
  "Словарь, боксы, опыт и награды сохранены — ничего не удаляется.";
/** ⚠ Про серию говорим честно: заморозки нет намеренно, иначе это лазейка. */
const STREAK =
  "Серию дней держат занятия, а не подписка: пока занятий нет, она прервётся.";

/**
 * Причина отказа. Всё, что не «человека в канале нет», считаем «не проверили» —
 * ⭐ по умолчанию НЕ обвиняем человека (в отказе может приехать что угодно).
 */
export function gateReason(access?: Access | null): GateReason {
  return access?.reason === "no_subscription" ? "no_subscription" : "unverified";
}

/** Что показать на экране. Чистая функция — её и проверяет `check-access.mjs`. */
export function gateView(access?: Access | null): GateView {
  const reason = gateReason(access);
  const url = access?.channel?.url || null;
  const open: GateAction = { label: "Открыть канал", act: "open" };
  const check: GateAction = { label: "Я подписался — проверить", act: "check" };

  if (reason === "no_subscription") {
    return {
      reason,
      title: "Приложение открыто подписчикам канала",
      lines: [
        "Подпишись на канал — и сразу возвращайся сюда, занятие уже собрано.",
        KEPT,
        STREAK,
      ],
      primary: url ? open : check,
      secondary: url ? check : null,
      url,
      retryHint:
        "Подписки пока не видим. Если только что подписался — подожди пару секунд " +
        "и нажми ещё раз.",
    };
  }

  return {
    reason,
    title: "Не смогли проверить подписку",
    lines: [
      "Telegram сейчас не отвечает. Дело не в тебе: проверить некому, поэтому вход закрыт.",
      KEPT,
      "Попробуй ещё раз через минуту — как только Telegram ответит, всё откроется само.",
    ],
    primary: { label: "Проверить ещё раз", act: "check" },
    secondary: url ? open : null,
    url,
    retryHint: "Telegram всё ещё молчит. Попробуй чуть позже — прогресс никуда не денется.",
  };
}

/** Экранирование: адрес приходит из настроек сервера, но в разметку без этого нельзя. */
export function esc(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function button(action: GateAction, primary: boolean): string {
  return `<button type="button" class="gatebtn${primary ? " primary" : ""}" ` +
         `data-act="${action.act}">${esc(action.label)}</button>`;
}

/** Разметка экрана. ⛔ Ни навигации, ни экранов приложения здесь нет по построению. */
export function gateHtml(view: GateView): string {
  return (
    '<main class="scroll"><div class="wrap"><div class="gate">' +
      '<div class="gate-mark" aria-hidden="true">🔒</div>' +
      `<h3>${esc(view.title)}</h3>` +
      view.lines.map((line) => `<p>${esc(line)}</p>`).join("") +
      '<div class="gate-acts">' +
        button(view.primary, true) +
        (view.secondary ? button(view.secondary, false) : "") +
      "</div>" +
      '<p class="gate-hint" id="gate-hint" role="status" aria-live="polite"></p>' +
    "</div></div></main>"
  );
}

/** Открыть канал: внутри Telegram — его же средствами, в браузере — новой вкладкой. */
function openChannel(url: string): void {
  const app = (window as unknown as {
    Telegram?: { WebApp?: { openTelegramLink?: (u: string) => void } };
  }).Telegram?.WebApp;
  try {
    if (app?.openTelegramLink) {
      app.openTelegramLink(url);
      return;
    }
  } catch {
    /* старый клиент — уйдём обычной ссылкой */
  }
  window.open(url, "_blank", "noopener");
}

/**
 * Показать замок вместо приложения.
 * @param recheck повторная попытка входа; `true` = доступ открылся (экран уже не наш).
 */
export function showGate(access: Access | null | undefined,
                         recheck: () => Promise<boolean>): void {
  const shell = document.getElementById("shell");
  if (!shell) return;
  const view = gateView(access);
  shell.innerHTML = gateHtml(view);

  const hint = shell.querySelector<HTMLElement>("#gate-hint");
  shell.querySelectorAll<HTMLButtonElement>("[data-act]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      if (btn.dataset.act === "open") {
        if (view.url) openChannel(view.url);
        // ⭐ Подписка происходит В ДРУГОМ окне, и вернувшись человек жмёт «проверить».
        // Подсказываем это сразу, чтобы он не тыкал «Открыть канал» второй раз.
        if (hint && view.secondary) hint.textContent = "Подписался? Нажми «проверить».";
        return;
      }
      btn.disabled = true;
      const was = btn.textContent;
      btn.textContent = "Проверяем…";
      if (hint) hint.textContent = "";
      let opened = false;
      try {
        opened = await recheck();
      } catch {
        opened = false;
      }
      if (opened) return;                     // приложение уже перерисовало себя само
      btn.disabled = false;
      btn.textContent = was;
      if (hint) hint.textContent = view.retryHint;
    });
  });
}
